import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { FiUploadCloud } from 'react-icons/fi';
import { DOCUMENT_TYPES } from '../../utils/constants';
import FormField from '../common/FormField';
import Alert from '../common/Alert';
import './DocumentUpload.css';

const DocumentUpload = ({ onUpload }) => {
  const [docType, setDocType] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const fileInputRef = useRef(null);

  const typeOptions = Object.entries(DOCUMENT_TYPES).map(([key, label]) => ({
    value: key, label
  }));

  const validateFile = (file) => {
    if (!file) return 'Please select a file.';
    if (!docType) return 'Please select a document type first.';
    
    const validTypes = ['application/pdf', 'image/jpeg', 'image/png'];
    if (!validTypes.includes(file.type)) {
      return 'Invalid file type. Please upload a PDF, JPG, or PNG.';
    }
    
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      return 'File is too large. Maximum size is 10MB.';
    }
    
    return null;
  };

  const handleFileUpload = async (file) => {
    const errorMsg = validateFile(file);
    if (errorMsg) {
      setError(errorMsg);
      return;
    }

    setError('');
    setUploading(true);
    setProgress(0);

    try {
      // onUpload reports real progress (0-100) through the third argument.
      await onUpload(file, docType, setProgress);
      setProgress(100);
      setDocType('');
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (err) {
      setError(err.message || 'Failed to upload document.');
    } finally {
      setTimeout(() => {
        setUploading(false);
        setProgress(0);
      }, 500);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileUpload(e.dataTransfer.files[0]);
    }
  };

  const handleChange = (e) => {
    e.preventDefault();
    if (e.target.files && e.target.files[0]) {
      handleFileUpload(e.target.files[0]);
    }
  };

  return (
    <div className="document-upload">
      <FormField
        label="Select Document Type"
        name="docType"
        type="select"
        options={typeOptions}
        value={docType}
        onChange={(e) => setDocType(e.target.value)}
        required
      />

      {error && <Alert type="error" message={error} dismissible onClose={() => setError('')} />}

      <div 
        className={`upload-zone ${dragActive ? 'drag-active' : ''} ${!docType ? 'disabled' : ''}`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={() => docType && fileInputRef.current?.click()}
        role="button"
        tabIndex={docType ? 0 : -1}
        aria-disabled={!docType}
      >
        <input
          ref={fileInputRef}
          type="file"
          className="sr-only"
          accept=".pdf,.jpg,.jpeg,.png"
          onChange={handleChange}
          disabled={!docType || uploading}
        />
        <FiUploadCloud className="upload-icon" />
        <p className="upload-text">
          {uploading ? 'Uploading...' : 'Drag and drop your file here, or click to browse'}
        </p>
        <p className="upload-hint">Supported formats: PDF, JPG, PNG (Max 10MB)</p>
        
        {uploading && (
          <div className="upload-progress-container">
            <div className="upload-progress-bar" style={{ width: `${progress}%` }}></div>
          </div>
        )}
      </div>
    </div>
  );
};

DocumentUpload.propTypes = {
  onUpload: PropTypes.func.isRequired
};

export default DocumentUpload;
